import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ArrowLeft, Loader2, Search, Users, Wind, X } from 'lucide-react';
import PatientSearchSelect, { getPatientName, isPatientOperationallyActive } from '../../../components/common/PatientSearchSelect';
import type { UnifiedPatient } from '../../../types/unified-financial';

export interface PeakFlowDiaryDialogProps {
  patients: UnifiedPatient[];
  patientsLoading: boolean;
  selectedPatientId: string;
  onPatientSelect: (patientId: string) => void;
  onClose: () => void;
  onBack?: () => void;
  children?: ReactNode;
}

export default function PeakFlowDiaryDialog({ patients, patientsLoading, selectedPatientId, onPatientSelect, onClose, onBack, children }: PeakFlowDiaryDialogProps) {
  const selectablePatients = patients.filter(patient => patient.id !== 'all' && patient.id !== '__outro__' && isPatientOperationallyActive(patient));
  const selectedPatient = selectablePatients.find(patient => patient.id === selectedPatientId);
  const activePatientId = selectedPatient?.id ?? '';
  const [mobilePickerOpen, setMobilePickerOpen] = useState(!activePatientId);
  const dialog = useRef<HTMLDivElement>(null);
  const picker = useRef<HTMLElement>(null);
  const content = useRef<HTMLElement>(null);
  const patientToggle = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);

  useEffect(() => {
    setMobilePickerOpen(!activePatientId);
    content.current?.scrollTo({ top: 0 });
    const frame = requestAnimationFrame(() => {
      if (activePatientId && window.matchMedia('(max-width: 767px)').matches) content.current?.focus();
      else picker.current?.querySelector<HTMLInputElement>('input')?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [activePatientId]);

  useEffect(() => {
    if (!patientsLoading && !activePatientId && document.activeElement === dialog.current) {
      picker.current?.querySelector<HTMLInputElement>('input')?.focus();
    }
  }, [patientsLoading, activePatientId]);

  useEffect(() => {
    const breakpoint = window.matchMedia('(min-width: 768px)');
    const restoreVisibleFocus = () => {
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && dialog.current?.contains(focused) && !focused.getClientRects().length) {
        if (picker.current?.getClientRects().length) picker.current.querySelector<HTMLInputElement>('input')?.focus();
        else patientToggle.current?.focus();
      }
    };
    breakpoint.addEventListener('change', restoreVisibleFocus);
    return () => breakpoint.removeEventListener('change', restoreVisibleFocus);
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) return;
    if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
    if (event.key !== 'Tab') return;
    const elements = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled):not([tabindex="-1"]), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), a[href], [tabindex="0"]') ?? [])
      .filter(element => element.tabIndex >= 0 && element.getClientRects().length > 0);
    const first = elements[0], last = elements[elements.length - 1];
    const active = document.activeElement;
    // Patient changes focus the content landmark, including loading/error views
    // with no controls. Resolve Tab from that landmark inside the modal too.
    if (active instanceof HTMLElement && active !== dialog.current && !elements.includes(active)) {
      event.preventDefault();
      const following = elements.find(element => active.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING);
      const preceding = [...elements].reverse().find(element => active.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING);
      (event.shiftKey ? preceding ?? last : following ?? first)?.focus();
      return;
    }
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) { event.preventDefault(); first?.focus(); }
  };

  const selectPatient = (id: string) => {
    onPatientSelect(id);
    setMobilePickerOpen(false);
    content.current?.scrollTo({ top: 0 });
    if (window.matchMedia('(max-width: 767px)').matches) requestAnimationFrame(() => content.current?.focus());
  };

  const toggleMobilePicker = () => {
    setMobilePickerOpen(value => !value);
    requestAnimationFrame(() => {
      if (!mobilePickerOpen) picker.current?.querySelector<HTMLInputElement>('input')?.focus();
      else content.current?.focus();
    });
  };

  return <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/45 p-2 sm:p-4">
    <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby="peak-flow-diary-title" tabIndex={-1} onKeyDown={handleKeyDown}
      className="flex h-[calc(100dvh-1rem)] max-h-[1000px] w-full max-w-[1600px] flex-col overflow-hidden rounded-2xl bg-slate-50 shadow-xl outline-none sm:h-[calc(100dvh-2rem)]">
      <header className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">{onBack && <button type="button" onClick={onBack} className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700 hover:text-primary-900"><ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />Nova avaliação</button>}<h2 id="peak-flow-diary-title" className="text-xl font-bold text-slate-900 sm:text-2xl">Diário de peak flow</h2>
            <p className="mt-1 text-sm text-slate-500">Acompanhe as aferições e gerencie o link de cada paciente.</p></div>
          <button type="button" onClick={onClose} aria-label="Fechar diário" className="shrink-0 rounded-xl p-2 text-slate-600 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600"><X className="h-5 w-5" /></button>
        </div>
        {selectedPatient && <button ref={patientToggle} type="button" onClick={toggleMobilePicker} aria-expanded={mobilePickerOpen} aria-controls="peak-flow-diary-patients"
          className="mt-3 flex w-full min-w-0 items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-left text-sm font-semibold text-primary-700 md:hidden">
          {mobilePickerOpen ? <ArrowLeft className="h-4 w-4 shrink-0" /> : <Users className="h-4 w-4 shrink-0" />}
          <span className="shrink-0">{mobilePickerOpen ? 'Ver diário' : 'Trocar paciente'}</span><span className="min-w-0 truncate font-normal text-slate-600">· {getPatientName(selectedPatient)}</span>
        </button>}
      </header>
      <div className="flex min-h-0 flex-1">
        <aside ref={picker} id="peak-flow-diary-patients" aria-label="Selecionar paciente do diário"
          className={`${mobilePickerOpen ? 'flex' : 'hidden'} min-h-0 w-full flex-col border-slate-200 bg-white p-4 md:flex md:w-[300px] md:shrink-0 md:border-r lg:w-[340px] lg:p-5`}>
          <div className="mb-3 flex shrink-0 items-center justify-between gap-2"><h3 className="font-bold text-slate-900">Pacientes</h3>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{selectablePatients.length} ativos</span></div>
          {patientsLoading && selectablePatients.length === 0 ? <p role="status" className="flex items-center gap-2 py-4 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Carregando pacientes…</p>
            : <PatientSearchSelect patients={selectablePatients} selectedPatientId={activePatientId} onSelect={selectPatient} presentation="inline"
                showActiveFilter={false} allowManualOther={false} allowInactiveSelection={false} placeholder="Buscar nome, CPF ou prontuário…" className="min-h-0 flex-1" />}
          <p className="mt-3 shrink-0 text-xs leading-relaxed text-slate-500">Selecione um paciente ativo para abrir ou criar seu acompanhamento.</p>
        </aside>
        <main ref={content} tabIndex={-1} aria-label="Acompanhamento do paciente"
          className={`${mobilePickerOpen ? 'hidden' : 'block'} min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain p-4 outline-none md:block lg:p-6`}>
          {selectedPatient ? children : <div className="flex h-full min-h-[240px] items-center justify-center">
            <div className="max-w-md rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-primary-700"><Wind className="h-6 w-6" /></div>
              <h3 className="mt-5 text-lg font-bold text-slate-900">Escolha um paciente</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">Busque pelo nome, CPF ou prontuário na lista ao lado para consultar as medidas ou iniciar um acompanhamento.</p>
              <div className="mt-5 inline-flex items-center gap-2 text-xs font-medium text-slate-500"><Search className="h-4 w-4" />Busca disponível em toda a lista de pacientes ativos</div>
            </div>
          </div>}
        </main>
      </div>
    </div>
  </div>;
}
